import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ArrowDown } from 'lucide-react';
import { HeroLines, ScrollLines, Reveal, Eyebrow } from '@/components/Reveal';
import SpiceMarquee from '@/components/Marquee';
import CtaBand from '@/components/CtaBand';
import { SPICES } from '@/data/spices';

const HERO_IMG =
  'https://images.pexels.com/photos/4198654/pexels-photo-4198654.jpeg?auto=compress&cs=tinysrgb&w=2000';
const JAR_IMG =
  'https://images.unsplash.com/photo-1598712586188-27853d8ccf0a?q=85&w=1200&auto=format&fit=crop';

const HERO_PROMISES = ['Farm-gate sourcing', 'Indicative quotes in 24h', 'FOB · Ex-warehouse'];

const BENTO = [
  {
    n: '02',
    title: 'Quotes inside a day',
    body: 'Send a spec, get an indicative FOB or ex-warehouse number within one business day — priced against the live market, not a stale rate card.',
  },
  {
    n: '03',
    title: 'Lab before loading',
    body: 'Moisture, curcumin, ASTA and micro tests on every lot. The certificate travels with the shipment.',
  },
  {
    n: '04',
    title: 'Terms that flex',
    body: 'Full containers or LCL consolidation on FOB terms; ex-warehouse or delivered within India. Volume breaks published against your shipment plan.',
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);

  return (
    <div className="bg-night text-bone">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-end overflow-hidden" data-testid="hero">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0" aria-hidden="true">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-night/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/20 to-night/60" />
        </motion.div>

        <div className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-40 md:px-12 md:pb-20">
          <Reveal delay={0.05} y={12}>
            <Eyebrow>Equinox Spices · Turmeric & Cardamom trading house</Eyebrow>
          </Reveal>

          <HeroLines
            className="mt-6"
            lineClassName="font-serif text-[clamp(2.9rem,8.5vw,7.5rem)] font-medium leading-[0.98] tracking-tight text-bone"
            lines={[
              'From Indian soil,',
              'to global shelves —',
              <em key="e" className="not-italic text-turmeric">priced to move.</em>,
            ]}
          />

          <Reveal delay={0.55} className="mt-8 max-w-xl">
            <p className="text-base leading-relaxed text-bone/80 md:text-lg">
              We buy turmeric and cardamom at the farm gate and put them on the water for buyers
              worldwide. Salem to Sangli, Idukki to Wayanad — direct origin, lab-backed lots,
              quotes that hold.
            </p>
          </Reveal>

          <Reveal delay={0.7} className="mt-10 flex flex-col gap-4 sm:flex-row">
            <Link
              to="/contact"
              data-testid="hero-cta-request-quote"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-turmeric px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-night transition-all duration-300 hover:scale-[1.03] hover:bg-bone"
            >
              Request a Quote
              <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
            </Link>
            <Link
              to="/products"
              data-testid="hero-cta-explore"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-bone/30 px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-bone transition-all duration-300 hover:border-turmeric hover:text-turmeric"
            >
              Explore the Spices
            </Link>
          </Reveal>

          <Reveal delay={0.9} className="mt-16 border-t border-bone/15 pt-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="hero-promises">
              {HERO_PROMISES.map((p) => (
                <p key={p} className="text-[11px] font-medium uppercase tracking-[0.26em] text-bone/70">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div
          aria-hidden="true"
          className="absolute right-6 top-1/2 hidden -translate-y-1/2 flex-col items-center gap-3 md:flex"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-bone/60" style={{ writingMode: 'vertical-rl' }}>
            Scroll
          </span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
            <ArrowDown className="h-4 w-4 text-turmeric" strokeWidth={1.5} />
          </motion.span>
        </motion.div>
      </section>

      <SpiceMarquee />

      {/* POSITIONING BENTO */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="positioning-section">
        <Reveal>
          <Eyebrow>The competitive edge</Eyebrow>
        </Reveal>
        <ScrollLines
          className="mt-6 block"
          lineClassName="font-serif text-4xl font-medium leading-[1.05] text-bone sm:text-5xl lg:text-6xl"
          lines={['Why buyers switch', 'to EQUINOX Spices.']}
        />

        <div className="mt-14 grid gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-7">
            <div className="flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-coal/40 p-8 transition-transform duration-500 hover:-translate-y-1 md:p-12" data-testid="bento-pricing">
              <div>
                <span className="font-serif text-lg italic text-turmeric">01</span>
                <h3 className="mt-4 max-w-lg font-serif text-3xl font-medium leading-tight text-bone md:text-4xl">
                  Priced at the farm gate, not after five middlemen.
                </h3>
                <p className="mt-5 max-w-lg text-sm leading-relaxed text-dune md:text-base">
                  Most spice trades pass brokers, aggregators and commission agents before they see
                  a port. Ours don’t. We contract directly with farmer groups and estates at
                  origin, so the margin someone else spends on middlemen becomes your price
                  advantage.
                </p>
              </div>
              <Link
                to="/about"
                className="mt-8 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-turmeric transition-colors duration-300 hover:text-bone"
                data-testid="bento-about-link"
              >
                How we source
                <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="md:col-span-5">
            <div className="relative h-full min-h-[320px] overflow-hidden rounded-3xl border border-white/10" data-testid="bento-image">
              <img
                src={JAR_IMG}
                alt="Turmeric powder in a jar on dark slate"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-transparent to-transparent" />
              <p className="absolute bottom-6 left-6 right-6 text-[11px] uppercase tracking-[0.26em] text-bone/85">
                Single-origin turmeric · lab-graded
              </p>
            </div>
          </Reveal>

          {BENTO.map((b, i) => (
            <Reveal key={b.n} delay={0.05 * (i + 1)} className="md:col-span-4">
              <div className="h-full rounded-3xl border border-white/10 bg-coal/40 p-8 transition-transform duration-500 hover:-translate-y-1 md:p-10">
                <span className="font-serif text-lg italic text-turmeric">{b.n}</span>
                <h3 className="mt-4 font-serif text-2xl font-medium text-bone md:text-3xl">{b.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-dune">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SPICE SHOWCASE */}
      <section className="border-t border-white/10 bg-coal/30">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="showcase-section">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <Reveal>
                <Eyebrow>The ledger</Eyebrow>
              </Reveal>
              <ScrollLines
                className="mt-6 block"
                lineClassName="font-serif text-4xl font-medium leading-[1.05] text-bone sm:text-5xl lg:text-6xl"
                lines={['What we move.']}
              />
            </div>
            <Reveal delay={0.15}>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-turmeric transition-colors duration-300 hover:text-bone"
                data-testid="showcase-view-all"
              >
                Full catalogue
                <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-12">
            {SPICES.map((s) => (
              <Reveal key={s.slug} className="md:col-span-6">
                <Link
                  to={`/contact?spice=${s.name}`}
                  data-testid={`spice-card-${s.slug}`}
                  className="group relative block h-[460px] overflow-hidden rounded-[10rem_10rem_2rem_2rem] border border-white/10 md:h-[560px]"
                >
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-night/90 via-night/20 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-8 md:p-10">
                    <p className="text-[11px] uppercase tracking-[0.26em] text-bone/70">{s.origin}</p>
                    <div className="mt-2 flex items-center justify-between gap-4">
                      <h3 className="font-serif text-4xl font-medium text-bone md:text-5xl">{s.name}</h3>
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-bone/30 transition-all duration-300 group-hover:border-turmeric group-hover:bg-turmeric group-hover:text-night">
                        <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                      </span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {s.varieties.map((v) => (
                        <span
                          key={v}
                          className="rounded-full border border-bone/25 bg-night/40 px-3.5 py-1.5 text-[12px] tracking-wide text-bone/90 backdrop-blur-sm"
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SUPPLY */}
      <section className="border-y border-white/10 bg-coal/30" data-testid="who-we-supply-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32">
          <div className="md:col-span-5">
            <Reveal>
              <Eyebrow>Who we supply</Eyebrow>
            </Reveal>
            <ScrollLines
              className="mt-6 block"
              lineClassName="font-serif text-4xl font-medium leading-[1.05] text-bone sm:text-5xl"
              lines={['Export or domestic.', 'Same lot, same standard.']}
            />
            <Reveal delay={0.2} className="mt-8 max-w-md text-sm leading-relaxed text-dune md:text-base">
              <p>
                The trade desk serves overseas importers and Indian buyers alike. Whether the
                consignment clears a foreign port or reaches a processing unit in Tamil Nadu, it
                ships from the same graded, lab-tested lots.
              </p>
            </Reveal>
          </div>
          <div className="grid gap-6 md:col-span-7 sm:grid-cols-2">
            {[
              {
                id: 'export',
                label: 'Export buyers',
                title: 'Importers, distributors & brands overseas',
                body: 'Full-container and LCL shipments on FOB terms. Phytosanitary, COA and origin documents prepared for customs at destination.',
                tags: ['FOB terms', 'Export documentation', 'Multi-origin lots'],
              },
              {
                id: 'domestic',
                label: 'Domestic buyers',
                title: 'Processors, wholesalers & retailers in India',
                body: 'Bulk and recurring supply within India, delivered ex-warehouse or to your door. GST-compliant invoicing with the same grading and lab reports as our export lots.',
                tags: ['Pan-India dispatch', 'GST invoicing', 'Recurring contracts'],
              },
            ].map((b, i) => (
              <Reveal key={b.id} delay={0.1 * i}>
                <div className="flex h-full flex-col rounded-3xl border border-white/10 bg-night/60 p-8" data-testid={`supply-card-${b.id}`}>
                  <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-turmeric">{b.label}</span>
                  <h3 className="mt-5 font-serif text-2xl font-medium leading-tight text-bone md:text-3xl">{b.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-dune">{b.body}</p>
                  <div className="mt-auto flex flex-wrap gap-2 pt-8">
                    {b.tags.map((t) => (
                      <span key={t} className="rounded-full border border-white/10 px-3 py-1 text-[11px] tracking-wide text-bone/70">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FLOW */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="quote-flow-section">
        <Reveal>
          <Eyebrow>Request a quote</Eyebrow>
        </Reveal>
        <ScrollLines
          className="mt-6 block"
          lineClassName="font-serif text-4xl font-medium leading-[1.05] text-bone sm:text-5xl lg:text-6xl"
          lines={['Three lines.', 'One quote.']}
        />

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {[
            ['01', 'Tell us the shipment', 'Spice, grade, volume, destination — one short form, saved directly to our trade desk.'],
            ['02', 'We confirm the lot', 'Grade, lab specs, packing and incoterms confirmed against current origin market rates.'],
            ['03', 'You get the number', 'An indicative quote within one business day. Samples and lab certificates on request.'],
          ].map(([n, title, body], i) => (
            <Reveal key={n} delay={0.08 * i}>
              <div className="h-full rounded-3xl border border-white/10 bg-coal/40 p-8 md:p-10">
                <span className="font-serif text-5xl italic text-turmeric/80">{n}</span>
                <h3 className="mt-6 font-serif text-2xl font-medium text-bone md:text-3xl">{title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-dune">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-12">
          <Link
            to="/contact"
            data-testid="quote-flow-cta"
            className="inline-flex items-center gap-2 rounded-full bg-turmeric px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.2em] text-night transition-all duration-300 hover:scale-[1.03] hover:bg-bone"
          >
            Start a Quote
            <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </Reveal>
      </section>

      <CtaBand />
    </div>
  );
}
